import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import React, {
  ForwardedRef, forwardRef, MutableRefObject, useEffect, useMemo, useRef, useState
} from 'react'
import { Overlay, Tooltip } from 'react-bootstrap'
import { connect } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import { actions } from '../../actions'
import * as selectors from '../../selectors'
import { StoreDispatch } from '../../store'
import * as types from '../../store/types'
import { useIsSmallScreen } from '../hooks'
import { Control } from './Control'
import { DropdownIndicator } from './DropdownIndicator'
import { MultiValueLabel } from './MultiValueLabel'
import { Placeholder } from './Placeholder'
import { SelectContainer } from './SelectContainer'
import { FiltersOptionType, TextSearchMode } from './types'
import { arrayWrapSelection } from './utils'
import { ValueContainer } from './ValueContainer'




export type SearchComponentType = CreatableSelect<FiltersOptionType>

const mapStateToProps = (
  state: types.RootState,
  ownProps: {listId: types.ListIdentifier},
) => ({
  currentFilters: state.listViews[ownProps.listId].filters,
  filterOptions: selectors.getFilterOptions(state, ownProps.listId),
  visibleSubtypes: selectors.getVisibleItemSubtypeOptions(
    state,
    ownProps.listId,
  ),
  visibleItems: selectors.getVisibleItems(state, ownProps.listId),
  allItemNames: selectors.allItemNames(state, ownProps.listId),
  selectedItemId: state.selectedItemId,
})

const mapDispatchToProps = (
  dispatch: StoreDispatch,
  ownProps: {listId: types.ListIdentifier},
) => ({
  updateFilters: (filterState: Partial<types.ItemFilterState>) =>
    dispatch(actions.updateFilterState(filterState, ownProps.listId)),
})

interface FiltersProps {
  className?: string
  listId: types.ListIdentifier
  currentFilters: types.ItemFilterState
  filterOptions: types.SaddlebagFilterOptions
  visibleSubtypes: string[]
  updateFilters: ReturnType<typeof mapDispatchToProps>['updateFilters']
  visibleItems: types.GriffonItem[]
  allItemNames: {id: string; name: string}[]
  navigateToItem: null | ((item: types.GriffonItem | string) => void)
  selectedItemId: string | null
}

interface ChoiceLabel {
  label: string
}

const attunementChoices: Array<types.ISearchTag<'requiresAttunement', boolean> &
  ChoiceLabel> = [
  {value: true, label: 'Requires attunement', type: 'requiresAttunement'},
  {value: false, label: 'No attunement', type: 'requiresAttunement'},
]

const freeChoices: Array<types.ISearchTag<'isFree', boolean> & ChoiceLabel> = [
  {value: true, label: 'Available for free', type: 'isFree'},
  {value: false, label: 'Patrons only', type: 'isFree'},
]

const rarityChoices: Array<types.RaritySearchTag & ChoiceLabel> = ([
  'common',
  'uncommon',
  'rare',
  'very rare',
  'legendary',
  'artifact',
  '__multiple_rarities__',
] as const).map(value => ({
  type: 'rarity',
  value,
  label:
    value === '__multiple_rarities__'
      ? 'Multiple rarities'
      : value.replace(/^./, l => l.toUpperCase()),
}))

const significanceChoices: Array<types.ISearchTag<'significance', string> &
  ChoiceLabel> = ['major', 'minor'].map(value => ({
  type: 'significance',
  value,
  label: value.replace(/^./, l => l.toUpperCase()),
}))

const cardVolumeChoices: Array<types.ISearchTag<'cardVolume', number> &
  ChoiceLabel> = [1, 2, 3, 4, 5, 6, 7].map(volume => ({
  type: 'cardVolume',
  label: `Card Volume ${volume} / Deck ${volume} / Pack ${volume}`,
  value: volume,
}))

const bookVolumeChoices: Array<types.ISearchTag<'bookVolume', number> &
  ChoiceLabel> = [1, 2, 3, 4].map(volume => ({
  type: 'bookVolume',
  label: `Book ${volume}`,
  value: volume,
}))

const textSearchModeLabel = (mode: TextSearchMode): string =>
  ({
    'text:nameAndMeta': 'Name/Meta',
    'text:description': 'Description',
    'text:flavour': 'Flavor Text',
  }[mode])

// Helper to check if a search tag type supports the "+" prefix (required indicator)
const supportsRequiredPrefix = (
  type: types.SearchTag['type'] | undefined,
): boolean => {
  if (!type) return false
  return (
    type === 'tag' ||
    type === 'text:nameAndMeta' ||
    type === 'text:description' ||
    type === 'text:flavour'
  )
}

// Helper to check if a value has the required prefix and remove it
const hasRequiredPrefix = (value: unknown): boolean =>
  typeof value === 'string' && value.startsWith('+')

const _Filters = (
  {
    currentFilters,
    filterOptions,
    updateFilters,
    allItemNames,
    visibleItems,
    navigateToItem,
    className,
    selectedItemId,
  }: FiltersProps,
  ref: ForwardedRef<SearchComponentType>,
) => {
  const inputRef = ref as MutableRefObject<SearchComponentType>
  const isSmallScreen = useIsSmallScreen()
  const [isFocused, setIsFocused] = useState(false)
  const itemTypeChoices: Array<types.ISearchTag<'itemType'> &
    ChoiceLabel> = filterOptions.itemType.map(t => ({
    value: t,
    label: t,
    type: 'itemType',
  }))
  const treasureThemeChoices: Array<types.ISearchTag<'treasureTheme', types.TreasureTheme> &
    ChoiceLabel> = filterOptions.treasureTheme.map(t => ({
    value: t,
    label: t,
    type: 'treasureTheme',
  }))
  const tagChoices: Array<types.ISearchTag<'tag'> & ChoiceLabel> =
    filterOptions.tags.map(t => ({
      value: t,
      label: t,
      type: 'tag',
    }))
  const nameChoices: Array<types.ISearchTag<'name'> & ChoiceLabel> = useMemo(
    () =>
      allItemNames.map(i => ({
        value: i.id,
        label: i.name,
        type: 'name',
      })),
    [allItemNames],
  )
  const [textSearchMode, setTextSearchMode] = useState<TextSearchMode>(
    'text:nameAndMeta',
  )
  const [inputValue, setInputValue] = useState<string>('')
  const isTypingRequired = inputValue.startsWith('+')

  const groupedOptions: Array<{label: string; options: FiltersOptionType[]}> =
    isTypingRequired
      ? [
          {
            label: 'Required tags',
            options: tagChoices.map(tag => ({
              ...tag,
              value: `+${tag.value}`, // Keep "+" in value for filtering
              label: tag.label, // Remove "+" from label (MultiValueLabel will add "Required tag | ")
            })),
          },
        ]
      : [
          {label: 'Item type', options: itemTypeChoices},
          {label: 'Attunement', options: attunementChoices},
          {label: 'Rarity', options: rarityChoices},
          {label: 'Subrarity', options: significanceChoices},
          {label: 'Available for free', options: freeChoices},
          {label: 'Card volume', options: cardVolumeChoices},
          {label: 'Book', options: bookVolumeChoices},
          {label: 'Treasure theme', options: treasureThemeChoices},
          {label: 'Tags', options: tagChoices},
        ]

  const hasFilters =
    currentFilters.attunementType.length ||
    currentFilters.itemType.length ||
    currentFilters.itemSubtype.length ||
    currentFilters.tagSearch.length
  if (navigateToItem !== null && !hasFilters) {
    groupedOptions.push({label: 'Jump to', options: nameChoices})
  }

  const escapeHandler = (event: globalThis.KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      if (isFocused) {
        inputRef?.current?.blur()
      } else if (!selectedItemId) {
        updateFilters({
          tagSearch: [],
        })
      }
    }
    if (event.key === 'Backspace' && !isFocused) {
      inputRef?.current?.focus()
    }
  }

  useEffect(() => {
    document.addEventListener('keyup', escapeHandler)

    return function cleanup() {
      document.removeEventListener('keyup', escapeHandler)
    }
  })

  // ISO string
  const [lastDismissedAt, setLastDismissedAt] = useState<string | null>(
    window.localStorage.getItem('dismissedSearchModeTooltipAt'),
  )

  const dismissTooltip = () => {
    const now = new Date().toISOString()
    setLastDismissedAt(now)
    window.localStorage.setItem('dismissedSearchModeTooltipAt', now)
  }

  const hasDismissedToday =
    new Date().toISOString().slice(0, 10) === lastDismissedAt?.slice(0, 10)

  const nextTextSearchMode =
    textSearchMode === 'text:nameAndMeta'
      ? 'text:description'
      : textSearchMode === 'text:description'
      ? 'text:flavour'
      : 'text:nameAndMeta'

  return (
    <>
      <CreatableSelect<FiltersOptionType>
        textSearchMode={textSearchMode}
        setTextSearchMode={setTextSearchMode}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        ref={ref}
        styles={{
          menu: provided => ({
            ...provided,
            border: 'none',
            boxShadow: 'none',
            zIndex: 1001, // just above OverlayTrigger (thumbnail/attunement tooltips)
          }),
          container: provided => {
            return {
              ...provided,
              width: isSmallScreen ? '300px' : '100%',
              maxWidth: '95vw',
            }
          },
          control: provided => {
            return {
              ...provided,
              ':hover': {
                borderColor: 'transparent',
              },
              borderColor: 'transparent',
              boxShadow: 'none',
              zIndex: 1002, // above menu's shadow
            }
          },
          placeholder: provided => {
            return {...provided, fontStyle: 'italic'}
          },
        }}
        isMulti
        placeholder={`${visibleItems.length} items`}
        name="text"
        components={{
          MultiValueLabel,
          SelectContainer,
          DropdownIndicator,
          Placeholder,
          Control,
          ValueContainer,
        }}
        tabSelectsValue={false}
        options={groupedOptions}
        formatOptionLabel={option => {
          // Display a shorter label, while keeping the original,
          // searchable label for the dropdown suggestion list
          if (option.type === 'cardVolume') {
            return `Vol. ${option.value}`
          }

          return option.label
        }}
        className={classnames(
          'basic-multi-select',
          'ledger-filters',
          className,
        )}
        classNamePrefix="select"
        isValidNewOption={input => {
          // Don't show creatable prompt when input is only "+" (empty after plus)
          if (input === '+') {
            return false
          }
          // Allow creating text search options with "+" prefix (required text search)
          // Disable creating new tag options when typing "+" (only show existing tags)
          if (input.startsWith('+') && input.length > 1) {
            // Allow if it's a text search (user can type "+term" to create required text search)
            // But don't allow creating new tags with "+" (they must select from existing)
            return true
          }
          return !!input
        }}
        createOptionPosition="first"
        onInputChange={(newValue, {action}) => {
          if (action === 'input-change') {
            setInputValue(newValue)
          }
        }}
        filterOption={(option, rawInput) => {
          // When typing "+", show tags (for selection) but also allow creating text searches
          if (rawInput.startsWith('+')) {
            const optionData: FiltersOptionType | {__isNew__: true} = option.data
            // Allow creating new text search options with "+" prefix
            if ('__isNew__' in optionData) {
              return true
            }
            // Show tag options when input starts with "+"
            if (optionData.type === 'tag') {
              const tagValue = optionData.value as string
              // Remove "+" from input for matching, but keep it in the option value
              const inputWithoutPlus = rawInput.slice(1).toLowerCase()
              return tagValue.toLowerCase().includes(inputWithoutPlus)
            }
            // Don't show other predefined options when typing "+"
            return false
          }

          const lowerInput = rawInput.toLowerCase()
          const optionData: FiltersOptionType | {__isNew__: true} = option.data

          if ('__isNew__' in optionData) return true

          const type = optionData.type
          const lowerLabel = optionData.label.toLowerCase()
          const lowerValue = `${optionData.value}`.toLowerCase()

          if (
            optionData.type === 'rarity' &&
            optionData.value === '__multiple_rarities__'
          )
            return 'multiple rarities / rarity varies'.includes(lowerInput)
          if (type === 'requiresAttunement')
            return 'attunement'.includes(lowerInput)
          if (type === 'isFree') return 'free / patron'.includes(lowerInput)
          if (type === 'name')
            return (
              rawInput.length > 0 &&
              navigateToItem !== null &&
              !hasFilters &&
              lowerLabel.includes(lowerInput)
            )

          return (
            lowerLabel.includes(lowerInput) || lowerValue.includes(lowerInput)
          )
        }}
        formatCreateLabel={input => {
          const isRequired = input.startsWith('+')
          const displayInput = isRequired ? input.slice(1) : input
          const requiredPrefix = isRequired ? 'Required ' : ''
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <em>
                {requiredPrefix}Search &ldquo;{displayInput}&rdquo; in{' '}
                {textSearchModeLabel(textSearchMode)}
              </em>

              {/* show on desktop only */}
              <TeachingTabIcon
                showTooltip={!hasDismissedToday}
                label={`Press TAB to search by ${textSearchModeLabel(
                  nextTextSearchMode,
                )}`}
              />
            </div>
          )
        }}
        value={currentFilters.tagSearch.map(t => {
          // Remove "+" prefix from display labels (MultiValueLabel will add appropriate prefix)
          let displayLabel: string
          if (t.type === 'requiresAttunement') {
            displayLabel = t.value ? 'Requires attunement' : 'No attunement'
          } else if (t.type === 'isFree') {
            displayLabel = t.value ? 'Available for free' : 'Patrons only'
          } else if (supportsRequiredPrefix(t.type) && hasRequiredPrefix(t.value)) {
            displayLabel = (t.value as string).slice(1) // Remove "+" prefix for display
          } else {
            displayLabel = t.value.toString()
          }
          return {
            ...t,
            label: displayLabel,
          }
        })}
        onChange={selection => {
          dismissTooltip()
          // Reset input value after selection to show all options again
          setInputValue('')
          
          const selectionList = arrayWrapSelection(selection)
          const firstSelection = selectionList[0]
          if (
            selectionList.length === 1 &&
            firstSelection &&
            firstSelection.type === 'name'
          ) {
            navigateToItem?.(firstSelection.value)
            return
          }

          const selectionListWithTextSearchModeApplied: types.SearchTag[] = selectionList.map(
            (s): types.SearchTag => {
              if (s.type !== undefined) {
                // Handle "+" prefix for tags and text searches (required indicator)
                // Keep the "+" prefix in the value for filtering logic
                if (supportsRequiredPrefix(s.type) && hasRequiredPrefix(s.value)) {
                  return s
                }
                return s
              }

              // Check if the input starts with "+" - could be required tag or required text search
              const inputValue = s.value as string
              if (hasRequiredPrefix(inputValue) && inputValue.length > 1) {
                // If we're in a text search mode, create as text search type with "+" prefix
                // Otherwise, create as tag type with "+" prefix (required tag)
                return {
                  type: textSearchMode,
                  value: inputValue, // Keep the "+" prefix
                }
              }

              return {
                ...s,
                type: textSearchMode,
              }
            },
          )

          updateFilters({
            tagSearch: selectionListWithTextSearchModeApplied,
          })
        }}
        onKeyDown={event => {
          if (event.key === 'Tab') {
            event.preventDefault()
            dismissTooltip()
            setTextSearchMode(nextTextSearchMode)
            return false
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false)
          // Reset input value on blur so it doesn't persist when refocusing
          setInputValue('')
          // event.preventDefault()
        }}
      />
    </>
  )
}

function TeachingTabIcon({
  showTooltip,
  label,
}: {
  showTooltip: boolean
  label: string
}) {
  const tabIconRef = useRef<HTMLSpanElement>(null)

  return (
    <>
      <span
        ref={tabIconRef}
        className="d-none d-lg-inline"
        style={{
          fontSize: 'smaller',
          border: '1px solid rgba(0, 0, 0, 0.5)',
          padding: '0.25rem',
          borderRadius: 4,
          backgroundColor: 'rgba(100, 100, 100, 0.1)',
          height: '30px',
          overflow: 'hidden',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          'text-wrap-mode': 'nowrap',
        }}
      >
        Tab <FontAwesomeIcon icon={faLongArrowAltRight} />
      </span>
      {tabIconRef.current && (
        <Overlay
          show={showTooltip}
          target={tabIconRef.current}
          placement="right"
        >
          <Tooltip id="search-mode-tooltip">{label}</Tooltip>
        </Overlay>
      )}
    </>
  )
}

const Filters = forwardRef(_Filters)

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(Filters)
